import {
  afterNextRender,
  Component,
  inject,
  Injector,
  input,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  FloatLabelType,
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatTimepicker,
  MatTimepickerModule,
} from '@angular/material/timepicker';
import { ErrorFieldComponent } from '../error-field/error-field.component';

@Component({
  selector: 'siat-timepicker-field',
  templateUrl: './timepicker-field.component.html',
  imports: [
    ReactiveFormsModule,
    ErrorFieldComponent,
    MatFormFieldModule,
    MatInputModule,
    MatTimepickerModule,
  ],
  providers: [provideNativeDateAdapter()],
})
export class TimepickerFieldComponent implements ControlValueAccessor {
  /**
   * The form group associated with the input field.
   */

  formGroup = input<FormGroup | null>(null);

  /**
   * The control name associated with the input field.
   */
  controlName = input<string | null>(null);

  /**
   * The id attribute of the input field.
   */
  id = input<string | null>(null);

  /**
   * The placeholder of the input field.
   */
  placeholder = input<string | null>(null);

  /**
   * The input label.
   */
  label = input<string | null>(null);

  /**
   * The input "name" attribute value.
   */
  name = input<string | null>(null);

  /**
   * The maxtime  for the input field.
   */
  maxTime = input<string | null>(null);

  /**
   * The mintime for the input field.
   */
  minTime = input<string | null>(null);

  /**
   * Hint shown below the field.
   */
  hint = input<string | null>(null);

  /**
   * The input appareance.
   */
  appearance = input<MatFormFieldAppearance | null>('outline');

  /**
   * The label float behaviour, 'always' keeps the placeholder visible when empty.
   */
  floatLabel = input<FloatLabelType>('auto');

  /**
   * Time (HH:mm) the empty field's list opens at, e.g. '07:00'; earlier times stay selectable.
   */
  initialScrollTime = input<string | null>(null);

  /**
   * Injector used to scroll the list once it is rendered.
   */
  private readonly injector = inject(Injector);

  /**
   * Gets the form control associated with the input field.
   */
  get control(): FormControl<Date | number> {
    return this.formGroup().controls[this.controlName()] as FormControl<
      Date | number
    >;
  }

  /**
   * @inheritdoc
   */
  onTouched: () => void = () => {};

  /**
   * @inheritdoc
   */
  onChange: (value: Date) => void = () => {};

  /**
   * The write value function.
   */
  writeValue(obj: Date): void {
    this.control.setValue(obj);
  }

  /**
   * The register on change function.
   */
  registerOnChange(changeCallback: (value: Date) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * The register on touched function.
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }

  /**
   * Scrolls the opened list to `initialScrollTime` when the field is empty,
   * right after Material scrolled it to its first option.
   */
  scrollToInitialTime(picker: MatTimepicker<Date>): void {
    const initialTime = this.initialScrollTime();
    if (!initialTime || this.control?.value) return;
    const initialMinutes = toMinutes(initialTime);
    afterNextRender(
      () => {
        const options =
          document
            .getElementById(picker.panelId)
            ?.querySelectorAll<HTMLElement>('mat-option') ?? [];
        Array.from(options)
          .find(
            (option) => toMinutes(option.textContent ?? '') >= initialMinutes,
          )
          ?.scrollIntoView({ block: 'start' });
      },
      { injector: this.injector },
    );
  }
}

/**
 * Minutes since midnight of an "HH:mm" (or "H.mm") label, -1 when it isn't a time.
 * @returns {number}
 */
function toMinutes(label: string): number {
  const match = label.match(/(\d{1,2})[:.](\d{2})/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : -1;
}
